import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiJson } from '@/lib/api';

type RateQueueItem = {
  id: number;
  public_id: string;
  created_at: string;
  side: string;
  category: string;
  body_text: string;
  status: string;
  source: string;
  type_tags: string[];
  reject_reason: string | null;
  times_shown: number;
  win_count: number;
  loss_count: number;
};

type RateQueueResponse = {
  items: RateQueueItem[];
  counts: { pending: number; approved: number; rejected: number };
};

type RateHostStat = {
  host: string;
  side: string;
  sessions: number;
  votes: number;
  unlocked: number;
  reports: number;
  visits_7d: number;
};

type RateTypeStat = {
  quiz_slug: string;
  type_key: string;
  count: number;
};

type RateTopScenario = {
  public_id: string;
  side: string;
  category: string;
  body_text: string;
  source: string;
  times_shown: number;
  win_count: number;
  loss_count: number;
  win_rate: number;
};

type RateDailyPoint = {
  date: string;
  sessions: number;
  votes: number;
  reports: number;
  submissions: number;
};

type RateStatsResponse = {
  days: number;
  votes_to_unlock: number;
  seed: { expected: number; present: number; missing: string[] };
  pool: {
    approved: number;
    pending: number;
    rejected: number;
    seed: number;
    ugc: number;
    by_side: Record<string, { approved: number; pending: number; rejected: number }>;
    by_category: Record<string, number>;
  };
  funnel: {
    sessions: number;
    sessions_7d: number;
    sessions_window: number;
    votes: number;
    votes_7d: number;
    votes_window: number;
    unlocked: number;
    reports: number;
    reports_window: number;
    unlock_rate: number;
    report_rate: number;
    avg_votes_per_session: number;
  };
  hosts: RateHostStat[];
  types: RateTypeStat[];
  top_scenarios: RateTopScenario[];
  daily: RateDailyPoint[];
};

type SeedResponse = {
  ok: boolean;
  created: number;
  updated: number;
  expected: number;
  present: number;
};

export const Route = createFileRoute('/admin/rate-queue')({
  component: RateQueuePage,
});

function formatNumber(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString();
}

function formatPercent(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(1)}%`;
}

function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>{label}</p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>{value}</p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

function RateQueuePage() {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [status, setStatus] = useState('pending');
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [seedMessage, setSeedMessage] = useState<string | null>(null);

  const statsQuery = useQuery({
    queryKey: ['admin', 'rate-stats'],
    queryFn: () => apiJson<RateStatsResponse>(getToken, '/v1/admin/rate/stats?days=14'),
    refetchInterval: 30_000,
  });

  const query = useQuery({
    queryKey: ['admin', 'rate-queue', status],
    queryFn: () =>
      apiJson<RateQueueResponse>(getToken, `/v1/admin/rate/queue?status=${status}&limit=80`),
  });

  const seed = useMutation({
    mutationFn: () =>
      apiJson<SeedResponse>(getToken, '/v1/admin/rate/seed', { method: 'POST' }),
    onSuccess: (result) => {
      setSeedMessage(
        `Seed pool ready: ${result.present}/${result.expected} Harbor scenarios (${result.created} new, ${result.updated} refreshed).`,
      );
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-stats'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-queue'] });
    },
    onError: (err) => {
      setSeedMessage(err instanceof Error ? err.message : 'Seed failed.');
    },
  });

  const approve = useMutation({
    mutationFn: (item: RateQueueItem) =>
      apiJson(getToken, `/v1/admin/rate/scenarios/${item.id}/approve`, {
        method: 'POST',
        body: JSON.stringify({ body_text: edits[item.id] ?? item.body_text }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-queue'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-stats'] });
    },
  });

  const reject = useMutation({
    mutationFn: (item: RateQueueItem) =>
      apiJson(getToken, `/v1/admin/rate/scenarios/${item.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: 'too_identifying' }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-queue'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-stats'] });
    },
  });

  const stats = statsQuery.data;
  const counts = query.data?.counts;
  const seedGap = stats ? stats.seed.expected - stats.seed.present : 0;

  return (
    <div className='space-y-8'>
      <div className='flex flex-wrap items-start justify-between gap-4'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-900'>Rate experiment</h1>
          <p className='mt-1 text-sm text-gray-600'>
            Pairwise voting on ratemycoparent.com and ratemycoparenting.com.
            Seed the Harbor scenario pool, watch the funnel, then review UGC.
          </p>
        </div>
        <button
          type='button'
          onClick={() => seed.mutate()}
          disabled={seed.isPending}
          className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60'
        >
          {seed.isPending ? 'Seeding…' : 'Seed / refresh Harbor scenarios'}
        </button>
      </div>
      {seedMessage ? <p className='text-sm text-gray-700'>{seedMessage}</p> : null}

      {statsQuery.isError ? (
        <p className='text-sm text-red-600'>Could not load experiment stats.</p>
      ) : null}

      <section className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
        <StatCard
          label='Sessions'
          value={formatNumber(stats?.funnel.sessions)}
          hint={`${formatNumber(stats?.funnel.sessions_7d)} in last 7 days`}
        />
        <StatCard
          label='Votes'
          value={formatNumber(stats?.funnel.votes)}
          hint={`Avg ${stats?.funnel.avg_votes_per_session ?? 0} per session`}
        />
        <StatCard
          label='Unlocked report'
          value={formatNumber(stats?.funnel.unlocked)}
          hint={`${formatPercent(stats?.funnel.unlock_rate)} of sessions reached ${stats?.votes_to_unlock ?? 8} votes`}
        />
        <StatCard
          label='Cards completed'
          value={formatNumber(stats?.funnel.reports)}
          hint={`${formatPercent(stats?.funnel.report_rate)} finished the quiz`}
        />
      </section>

      <section className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
        <StatCard
          label='Seed pool'
          value={
            stats
              ? `${formatNumber(stats.seed.present)} / ${formatNumber(stats.seed.expected)}`
              : '—'
          }
          hint={
            seedGap > 0
              ? `${seedGap} missing — run seed`
              : 'Harbor-written scenarios in the vote pool'
          }
        />
        <StatCard
          label='Approved pool'
          value={formatNumber(stats?.pool.approved)}
          hint={`${formatNumber(stats?.pool.seed)} seed · ${formatNumber(stats?.pool.ugc)} user texts`}
        />
        <StatCard
          label='UGC pending'
          value={formatNumber(stats?.pool.pending)}
          hint={`${formatNumber(stats?.pool.rejected)} rejected`}
        />
        <StatCard
          label='Last 14 days'
          value={formatNumber(stats?.funnel.votes_window)}
          hint={`${formatNumber(stats?.funnel.sessions_window)} sessions · ${formatNumber(stats?.funnel.reports_window)} cards`}
        />
      </section>

      <section className='grid gap-4 lg:grid-cols-2'>
        <div className='rounded-xl border border-gray-200 bg-white p-4'>
          <h2 className='text-sm font-semibold text-gray-900'>By domain</h2>
          <table className='mt-3 w-full text-left text-sm'>
            <thead className='text-xs uppercase tracking-wide text-gray-500'>
              <tr>
                <th className='py-1 font-semibold'>Host</th>
                <th className='py-1 text-right font-semibold'>Visits 7d</th>
                <th className='py-1 text-right font-semibold'>Sessions</th>
                <th className='py-1 text-right font-semibold'>Votes</th>
                <th className='py-1 text-right font-semibold'>Cards</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.hosts ?? []).map((row) => (
                <tr key={row.host} className='border-t border-gray-100'>
                  <td className='py-2'>
                    <div className='font-medium text-gray-900'>{row.host}</div>
                    <div className='text-xs text-gray-500'>{row.side}</div>
                  </td>
                  <td className='py-2 text-right tabular-nums'>{formatNumber(row.visits_7d)}</td>
                  <td className='py-2 text-right tabular-nums'>{formatNumber(row.sessions)}</td>
                  <td className='py-2 text-right tabular-nums'>{formatNumber(row.votes)}</td>
                  <td className='py-2 text-right tabular-nums'>{formatNumber(row.reports)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='rounded-xl border border-gray-200 bg-white p-4'>
          <h2 className='text-sm font-semibold text-gray-900'>Result types</h2>
          {(stats?.types ?? []).length === 0 ? (
            <p className='mt-3 text-sm text-gray-500'>No cards yet.</p>
          ) : (
            <ul className='mt-3 space-y-2 text-sm'>
              {stats?.types.map((row) => (
                <li
                  key={`${row.quiz_slug}-${row.type_key}`}
                  className='flex items-center justify-between gap-3'
                >
                  <span className='text-gray-700'>
                    <span className='font-medium'>{row.type_key.replace(/_/g, ' ')}</span>
                    <span className='ml-2 text-xs text-gray-400'>{row.quiz_slug}</span>
                  </span>
                  <span className='tabular-nums text-gray-900'>{formatNumber(row.count)}</span>
                </li>
              ))}
            </ul>
          )}
          <div className='mt-4 text-xs text-gray-500'>
            Approved by category:{' '}
            {Object.entries(stats?.pool.by_category ?? {})
              .map(([name, count]) => `${name} ${count}`)
              .join(' · ') || '—'}
          </div>
        </div>
      </section>

      <section className='rounded-xl border border-gray-200 bg-white p-4'>
        <h2 className='text-sm font-semibold text-gray-900'>Daily (14d)</h2>
        <div className='mt-3 overflow-x-auto'>
          <table className='w-full text-left text-sm'>
            <thead className='text-xs uppercase tracking-wide text-gray-500'>
              <tr>
                <th className='py-1 font-semibold'>Date</th>
                <th className='py-1 text-right font-semibold'>Sessions</th>
                <th className='py-1 text-right font-semibold'>Votes</th>
                <th className='py-1 text-right font-semibold'>Cards</th>
                <th className='py-1 text-right font-semibold'>UGC</th>
              </tr>
            </thead>
            <tbody>
              {(stats?.daily ?? [])
                .slice()
                .reverse()
                .map((row) => (
                  <tr key={row.date} className='border-t border-gray-100'>
                    <td className='py-1.5 text-gray-700'>{row.date}</td>
                    <td className='py-1.5 text-right tabular-nums'>{formatNumber(row.sessions)}</td>
                    <td className='py-1.5 text-right tabular-nums'>{formatNumber(row.votes)}</td>
                    <td className='py-1.5 text-right tabular-nums'>{formatNumber(row.reports)}</td>
                    <td className='py-1.5 text-right tabular-nums'>
                      {formatNumber(row.submissions)}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className='rounded-xl border border-gray-200 bg-white p-4'>
        <h2 className='text-sm font-semibold text-gray-900'>Most shown scenarios</h2>
        {(stats?.top_scenarios ?? []).length === 0 ? (
          <p className='mt-3 text-sm text-gray-500'>Nothing in the pool yet. Seed first.</p>
        ) : (
          <ul className='mt-3 space-y-3'>
            {stats?.top_scenarios.map((row) => (
              <li key={row.public_id} className='border-t border-gray-100 pt-3 first:border-0 first:pt-0'>
                <p className='text-sm text-gray-900'>{row.body_text}</p>
                <p className='mt-1 text-xs text-gray-500'>
                  {row.side} · {row.category} · {row.source} · shown {row.times_shown} · win{' '}
                  {formatPercent(row.win_rate)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className='space-y-4'>
        <div>
          <h2 className='text-lg font-semibold text-gray-900'>UGC queue</h2>
          <p className='mt-1 text-sm text-gray-600'>
            Reject-by-default review. Approve only anonymous situations.
          </p>
          <p className='mt-2 text-sm text-gray-500'>
            Pending {counts?.pending ?? 0} · Approved {counts?.approved ?? 0} · Rejected{' '}
            {counts?.rejected ?? 0}
          </p>
        </div>
        <div className='flex gap-2'>
          {['pending', 'approved', 'rejected', 'all'].map((key) => (
            <button
              key={key}
              type='button'
              onClick={() => setStatus(key)}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                status === key ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {key}
            </button>
          ))}
        </div>
        <div className='space-y-4'>
          {(query.data?.items ?? []).map((item) => (
            <div key={item.id} className='rounded-xl border border-gray-200 bg-white p-4'>
              <div className='flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-gray-500'>
                <span>{item.side}</span>
                <span>{item.category}</span>
                <span>{item.source}</span>
                <span>{item.status}</span>
              </div>
              <textarea
                className='mt-3 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm'
                rows={3}
                value={edits[item.id] ?? item.body_text}
                onChange={(event) =>
                  setEdits((prev) => ({ ...prev, [item.id]: event.target.value }))
                }
              />
              <p className='mt-2 text-xs text-gray-500'>
                shown {item.times_shown} · wins {item.win_count} · losses {item.loss_count}
                {item.reject_reason ? ` · ${item.reject_reason}` : ''}
              </p>
              <div className='mt-3 flex gap-2'>
                <button
                  type='button'
                  onClick={() => approve.mutate(item)}
                  className='rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-white'
                >
                  Approve
                </button>
                <button
                  type='button'
                  onClick={() => reject.mutate(item)}
                  className='rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold text-gray-700'
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
          {query.data && query.data.items.length === 0 ? (
            <p className='text-sm text-gray-500'>Nothing in this queue.</p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
